import { useState, useMemo } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { format, isToday, isBefore, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Plus, Filter, MoreHorizontal, Receipt, Clock, AlertTriangle, RefreshCw, X, Check, Pencil, Trash2, Calendar, Search, ArrowUpDown, ChevronLeft, ChevronRight, Paperclip } from 'lucide-react';
import { parseDate } from '@/lib/localizedDateUtils';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';

import { MoneyDisplay, BusinessUnitSelector, FinancePageLayout } from '@/components/finance';
import { GenerateExpensesModal } from '@/components/finance/GenerateExpensesModal';
import type { BusinessUnit } from '@/components/finance/BusinessUnitSelector';
import { 
  useExpenses, useCreateExpense, useUpdateExpense, usePayExpense, type Expense 
} from '@/hooks/finance/useExpenses';
import { useExpenseCategories, groupCategories } from '@/hooks/finance/useExpenseCategories';
import { useBankAccounts } from '@/hooks/finance/useBankAccounts';
import { ExpensesDashboard } from '@/components/finance/expenses/ExpensesDashboard';
import { ExpensesExport } from '@/components/finance/expenses/ExpensesExport';
import { ExpenseAttachments } from '@/components/finance/expenses/ExpenseAttachments';

type ExpenseStatus = 'pending' | 'paid' | 'overdue' | 'cancelled';

const STATUS_CONFIG: Record<ExpenseStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending: { label: 'Pendente', variant: 'secondary' },
  paid: { label: 'Pago', variant: 'default' },
  overdue: { label: 'Atrasado', variant: 'destructive' },
  cancelled: { label: 'Cancelado', variant: 'outline' },
};

const RECURRENCE_OPTIONS = [
  { value: 'once', label: 'Uma vez' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensal' },
  { value: 'yearly', label: 'Anual' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50];

interface ExpenseFormData {
  business_unit: string;
  brand: string;
  category_id: string;
  bank_account_id: string;
  description: string;
  supplier_name: string;
  currency: string;
  amount: string;
  due_date: string;
  recurrence: string;
  recurrence_day: string;
  recurrence_end_date: string;
  notes: string;
}

const INITIAL_FORM_DATA: ExpenseFormData = {
  business_unit: 'us',
  brand: 'shared',
  category_id: '',
  bank_account_id: '',
  description: '',
  supplier_name: '',
  currency: 'USD',
  amount: '',
  due_date: safeFormat(new Date(), 'yyyy-MM-dd'),
  recurrence: 'once',
  recurrence_day: '',
  recurrence_end_date: '',
  notes: '',
};

type SortField = 'amount' | 'due_date' | 'status' | 'description';
type SortDir = 'asc' | 'desc';

export default function Expenses() {
  // Filters
  const [businessUnit, setBusinessUnit] = useState<BusinessUnit>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  
  // Pagination & Sort
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortField, setSortField] = useState<SortField>('due_date');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  
  // Modals
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  
  // Selected expense for actions
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [formData, setFormData] = useState<ExpenseFormData>(INITIAL_FORM_DATA);
  const [formAttachments, setFormAttachments] = useState<string[]>([]);
  
  // Pay modal state
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState(safeFormat(new Date(), 'yyyy-MM-dd'));
  const [payBankAccountId, setPayBankAccountId] = useState('');
  
  // Queries
  const { data: expenses = [], isLoading } = useExpenses({ 
    businessUnit,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    categoryId: categoryFilter !== 'all' ? categoryFilter : undefined,
    brand: brandFilter !== 'all' ? brandFilter : undefined,
    startDate: startDate ? safeFormat(startDate, 'yyyy-MM-dd') : undefined,
    endDate: endDate ? safeFormat(endDate, 'yyyy-MM-dd') : undefined,
    searchText: searchText.trim() || undefined,
  });
  const { data: categories = [] } = useExpenseCategories();
  const { data: bankAccounts = [] } = useBankAccounts(businessUnit === 'all' ? 'all' : businessUnit);
  
  // Mutations
  const createExpense = useCreateExpense();
  const updateExpense = useUpdateExpense();
  const payExpense = usePayExpense();
  
  // Computed
  const groupedCategories = useMemo(() => groupCategories(categories), [categories]);
  
  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return '-';
    const category = categories.find(c => c.id === categoryId);
    return category?.name || '-';
  };

  const stats = useMemo(() => {
    const today = startOfDay(new Date());
    let totalPending = 0;
    let dueToday = 0;
    let overdue = 0;
    
    expenses.forEach((expense) => {
      if (expense.status === 'paid' || expense.status === 'cancelled') return;
      const parsedDueDate = parseDate(expense.due_date);
      if (!parsedDueDate) return;
      const dueDate = startOfDay(parsedDueDate);
      const amount = expense.amount;
      totalPending += amount;
      if (isToday(dueDate)) dueToday += amount;
      else if (isBefore(dueDate, today)) overdue += amount;
    });
    
    return { totalPending, dueToday, overdue };
  }, [expenses]);

  // Client-side text filter + sort + pagination
  const filteredExpenses = useMemo(() => {
    let filtered = [...expenses];
    
    // Client-side text search (description & supplier)
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.description.toLowerCase().includes(q) ||
          (e.supplier_name && e.supplier_name.toLowerCase().includes(q))
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'amount': cmp = a.amount - b.amount; break;
        case 'due_date': cmp = a.due_date.localeCompare(b.due_date); break;
        case 'status': cmp = a.status.localeCompare(b.status); break;
        case 'description': cmp = a.description.localeCompare(b.description); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    
    return filtered;
  }, [expenses, searchText, sortField, sortDir]);
  
  const totalPages = Math.max(1, Math.ceil(filteredExpenses.length / pageSize));
  const paginatedExpenses = filteredExpenses.slice(page * pageSize, (page + 1) * pageSize);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(0);
  };
  
  // Handlers
  const handleOpenCreate = () => {
    setSelectedExpense(null);
    setFormData({
      ...INITIAL_FORM_DATA,
      currency: businessUnit === 'br' ? 'BRL' : 'USD',
      business_unit: businessUnit === 'all' ? 'us' : businessUnit,
    });
    setFormAttachments([]);
    setIsFormOpen(true);
  };
  
  const handleOpenEdit = (expense: Expense) => {
    setSelectedExpense(expense);
    setFormData({
      business_unit: expense.business_unit,
      brand: expense.brand || 'shared',
      category_id: expense.category_id || '',
      bank_account_id: expense.bank_account_id || '',
      description: expense.description,
      supplier_name: expense.supplier_name || '',
      currency: expense.currency,
      amount: expense.amount.toString(),
      due_date: expense.due_date,
      recurrence: expense.recurrence || 'once',
      recurrence_day: expense.recurrence_day?.toString() || '',
      recurrence_end_date: expense.recurrence_end_date || '',
      notes: expense.notes || '',
    });
    setFormAttachments(
      Array.isArray(expense.attachments) ? expense.attachments : []
    );
    setIsFormOpen(true);
  };
  
  const handleOpenPay = (expense: Expense) => {
    setSelectedExpense(expense);
    setPayAmount(expense.amount.toString());
    setPayDate(safeFormat(new Date(), 'yyyy-MM-dd'));
    setPayBankAccountId(expense.bank_account_id || '');
    setIsPayModalOpen(true);
  };
  
  const handleOpenCancel = (expense: Expense) => {
    setSelectedExpense(expense);
    setIsCancelDialogOpen(true);
  };
  
  const handleSubmitForm = async () => {
    if (!formData.description || !formData.amount || !formData.due_date) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    
    const payload = {
      business_unit: formData.business_unit,
      brand: formData.brand || undefined,
      category_id: formData.category_id || undefined,
      bank_account_id: formData.bank_account_id || undefined,
      description: formData.description,
      supplier_name: formData.supplier_name || undefined,
      currency: formData.currency,
      amount: parseFloat(formData.amount),
      due_date: formData.due_date,
      recurrence: formData.recurrence,
      recurrence_day: formData.recurrence_day ? parseInt(formData.recurrence_day) : undefined,
      recurrence_end_date: formData.recurrence_end_date || undefined,
      notes: formData.notes || undefined,
      attachments: formAttachments.length > 0 ? formAttachments : undefined,
    };
    
    try {
      if (selectedExpense) {
        await updateExpense.mutateAsync({ id: selectedExpense.id, ...payload });
        toast.success('Despesa atualizada com sucesso');
      } else {
        await createExpense.mutateAsync(payload);
        toast.success('Despesa criada com sucesso');
      }
      setIsFormOpen(false);
    } catch (error) {
      toast.error('Erro ao salvar despesa');
      console.error(error);
    }
  };
  
  const handleSubmitPay = async () => {
    if (!selectedExpense || !payAmount || !payDate) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    if (!payBankAccountId) {
      toast.error('Selecione a conta bancária para registrar a saída de caixa');
      return;
    }
    
    try {
      await payExpense.mutateAsync({
        id: selectedExpense.id,
        paid_amount: parseFloat(payAmount),
        paid_at: payDate,
        bank_account_id: payBankAccountId,
      });
      toast.success('Pagamento registrado com sucesso');
      setIsPayModalOpen(false);
    } catch (error) {
      toast.error('Erro ao registrar pagamento');
      console.error(error);
    }
  };
  
  const handleConfirmCancel = async () => {
    if (!selectedExpense) return;
    try {
      await updateExpense.mutateAsync({ id: selectedExpense.id, status: 'cancelled' });
      toast.success('Despesa cancelada');
      setIsCancelDialogOpen(false);
    } catch (error) {
      toast.error('Erro ao cancelar despesa');
      console.error(error);
    }
  };
  
  const getExpenseStatus = (expense: Expense): ExpenseStatus => {
    if (expense.status === 'paid') return 'paid';
    if (expense.status === 'cancelled') return 'cancelled';
    const parsedDueDate = parseDate(expense.due_date);
    if (!parsedDueDate) return 'pending';
    const dueDate = startOfDay(parsedDueDate);
    const today = startOfDay(new Date());
    if (isBefore(dueDate, today)) return 'overdue';
    return 'pending';
  };

  const SortButton = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <button
      className="flex items-center gap-1 hover:text-foreground transition-colors"
      onClick={() => handleSort(field)}
    >
      {children}
      <ArrowUpDown className={cn('h-3 w-3', sortField === field ? 'text-foreground' : 'text-muted-foreground/50')} />
    </button>
  );

  return (
    <FinancePageLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-foreground">Contas a Pagar</h1>
        <div className="flex gap-2">
          <ExpensesExport expenses={filteredExpenses} getCategoryName={getCategoryName} />
          <Button variant="outline" onClick={() => setIsGenerateModalOpen(true)}>
            <Calendar className="h-4 w-4 mr-2" />
            Gerar Lançamentos
          </Button>
          <Button onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Nova Despesa
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total a Pagar</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <MoneyDisplay value={stats.totalPending} currency="USD" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Vence Hoje</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              <MoneyDisplay value={stats.dueToday} currency="USD" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Em Atraso</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              <MoneyDisplay value={stats.overdue} currency="USD" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dashboard Charts */}
      <ExpensesDashboard expenses={expenses} getCategoryName={getCategoryName} />
      
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Business Unit</Label>
              <BusinessUnitSelector value={businessUnit} onChange={setBusinessUnit} includeAll />
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="paid">Pago</SelectItem>
                  <SelectItem value="overdue">Atrasado</SelectItem>
                  <SelectItem value="cancelled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(0); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {Object.entries(groupedCategories).map(([type, cats]) => (
                    <div key={type}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase">{type}</div>
                      {cats.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Marca</Label>
              <Select value={brandFilter} onValueChange={(v) => { setBrandFilter(v); setPage(0); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="shared">Compartilhado</SelectItem>
                  <SelectItem value="divus">Divus</SelectItem>
                  <SelectItem value="blueviza">BlueViza</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Data Início</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn('w-[140px] justify-start text-left font-normal', !startDate && 'text-muted-foreground')}>
                    {startDate ? safeFormat(startDate, 'dd/MM/yyyy') : 'Início'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent mode="single" selected={startDate} onSelect={(d) => { setStartDate(d); setPage(0); }} className="p-3 pointer-events-auto" />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Data Fim</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn('w-[140px] justify-start text-left font-normal', !endDate && 'text-muted-foreground')}>
                    {endDate ? safeFormat(endDate, 'dd/MM/yyyy') : 'Fim'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent mode="single" selected={endDate} onSelect={(d) => { setEndDate(d); setPage(0); }} className="p-3 pointer-events-auto" />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2 flex-1 min-w-[200px]">
              <Label>Busca</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Descrição ou fornecedor..."
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                  className="pl-9"
                />
              </div>
            </div>

            {(startDate || endDate || searchText || brandFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStartDate(undefined);
                  setEndDate(undefined);
                  setSearchText('');
                  setBrandFilter('all');
                  setPage(0);
                }}
              >
                <X className="h-4 w-4 mr-1" />
                Limpar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Expenses Table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredExpenses.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma despesa encontrada
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead><SortButton field="description">Descrição</SortButton></TableHead>
                    <TableHead>Fornecedor</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead><SortButton field="amount">Valor</SortButton></TableHead>
                    <TableHead><SortButton field="due_date">Vencimento</SortButton></TableHead>
                    <TableHead><SortButton field="status">Status</SortButton></TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedExpenses.map((expense) => {
                    const status = getExpenseStatus(expense);
                    const config = STATUS_CONFIG[status];
                    const hasAttachments = Array.isArray(expense.attachments) && expense.attachments.length > 0;
                    
                    return (
                      <TableRow key={expense.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {expense.recurrence && expense.recurrence !== 'once' && (
                              <RefreshCw className="h-3 w-3 text-muted-foreground" />
                            )}
                            {hasAttachments && (
                              <Paperclip className="h-3 w-3 text-muted-foreground" />
                            )}
                            {expense.description}
                          </div>
                        </TableCell>
                        <TableCell>{expense.supplier_name || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{getCategoryName(expense.category_id)}</Badge>
                        </TableCell>
                        <TableCell>
                          <MoneyDisplay value={expense.amount} currency={expense.currency as 'USD' | 'BRL'} />
                        </TableCell>
                        <TableCell>
                          {safeFormat(new Date(expense.due_date), 'dd/MM/yyyy', { locale: ptBR })}
                        </TableCell>
                        <TableCell>
                          <Badge variant={config.variant}>{config.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {status === 'pending' || status === 'overdue' ? (
                                <>
                                  <DropdownMenuItem onClick={() => handleOpenPay(expense)}>
                                    <Check className="h-4 w-4 mr-2" />Registrar Pagamento
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleOpenEdit(expense)}>
                                    <Pencil className="h-4 w-4 mr-2" />Editar
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleOpenCancel(expense)} className="text-destructive">
                                    <X className="h-4 w-4 mr-2" />Cancelar
                                  </DropdownMenuItem>
                                </>
                              ) : (
                                <DropdownMenuItem onClick={() => handleOpenEdit(expense)}>
                                  <Pencil className="h-4 w-4 mr-2" />Ver Detalhes
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{filteredExpenses.length} itens</span>
                  <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(Number(v)); setPage(0); }}>
                    <SelectTrigger className="w-[80px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s.toString()}>{s}/pág</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" className="h-8 w-8" disabled={page === 0} onClick={() => setPage(page - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {page + 1} / {totalPages}
                  </span>
                  <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Create/Edit Modal */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedExpense ? 'Editar Despesa' : 'Nova Despesa'}</DialogTitle>
            <DialogDescription>
              {selectedExpense ? 'Atualize os dados da despesa' : 'Preencha os dados para criar uma nova despesa'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Business Unit *</Label>
                <Select value={formData.business_unit} onValueChange={(v) => setFormData({ ...formData, business_unit: v, currency: v === 'br' ? 'BRL' : 'USD' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="us">US</SelectItem>
                    <SelectItem value="br">BR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Marca</Label>
                <Select value={formData.brand} onValueChange={(v) => setFormData({ ...formData, brand: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shared">Compartilhado</SelectItem>
                    <SelectItem value="divus">Divus</SelectItem>
                    <SelectItem value="blueviza">BlueViza</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Descrição *</Label>
              <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Ex: Aluguel escritório" />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(groupedCategories).map(([type, cats]) => (
                      <div key={type}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase">{type}</div>
                        {cats.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Conta Bancária</Label>
                <Select value={formData.bank_account_id} onValueChange={(v) => setFormData({ ...formData, bank_account_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {bankAccounts
                      .filter(acc => formData.business_unit === 'all' || acc.business_unit === formData.business_unit)
                      .map((acc) => (
                        <SelectItem key={acc.id} value={acc.id}>{acc.name} ({acc.currency})</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Fornecedor</Label>
              <Input value={formData.supplier_name} onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })} placeholder="Nome do fornecedor" />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Moeda</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="BRL">BRL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Valor *</Label>
                <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>Vencimento *</Label>
                <Input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Recorrência</Label>
                <Select value={formData.recurrence} onValueChange={(v) => setFormData({ ...formData, recurrence: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.recurrence === 'monthly' && (
                <div className="space-y-2">
                  <Label>Dia do Mês</Label>
                  <Input type="number" min="1" max="31" value={formData.recurrence_day} onChange={(e) => setFormData({ ...formData, recurrence_day: e.target.value })} placeholder="1-31" />
                </div>
              )}
              {formData.recurrence !== 'once' && (
                <div className="space-y-2">
                  <Label>Fim da Recorrência</Label>
                  <Input type="date" value={formData.recurrence_end_date} onChange={(e) => setFormData({ ...formData, recurrence_end_date: e.target.value })} />
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Notas adicionais..." rows={3} />
            </div>

            {/* Attachments */}
            <ExpenseAttachments
              expenseId={selectedExpense?.id}
              attachments={formAttachments}
              onAttachmentsChange={setFormAttachments}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmitForm} disabled={createExpense.isPending || updateExpense.isPending}>
              {(createExpense.isPending || updateExpense.isPending) && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              {selectedExpense ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Pay Modal */}
      <Dialog open={isPayModalOpen} onOpenChange={setIsPayModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Pagamento</DialogTitle>
            <DialogDescription>{selectedExpense?.description}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Valor a Pagar</Label>
              <div className="text-sm text-muted-foreground mb-1">
                Valor original: <MoneyDisplay value={selectedExpense?.amount || 0} currency={(selectedExpense?.currency as 'USD' | 'BRL') || 'USD'} />
              </div>
              <Input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Data do Pagamento</Label>
              <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Conta Bancária *</Label>
              <Select value={payBankAccountId} onValueChange={setPayBankAccountId}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {bankAccounts
                    .filter(acc => !selectedExpense || acc.business_unit === selectedExpense.business_unit)
                    .map((acc) => (
                      <SelectItem key={acc.id} value={acc.id}>{acc.name} ({acc.currency})</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPayModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmitPay} disabled={payExpense.isPending || !payBankAccountId}>
              {payExpense.isPending && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Cancel Confirmation */}
      <AlertDialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Despesa</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja cancelar a despesa "{selectedExpense?.description}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Cancelar Despesa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Generate Expenses Modal */}
      <GenerateExpensesModal open={isGenerateModalOpen} onOpenChange={setIsGenerateModalOpen} />
    </FinancePageLayout>
  );
}
